import React, { useCallback, useState } from "react";
import _ from "lodash";

import { EditIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { useCaching } from "utils/common";
import { format, FORMATS, parseISO } from "utils/datetime";

import Box from "components/Box";
import Button from "components/Button";
import Dialog, { DialogActions, DialogTitle } from "components/Dialog";
import FixedTable from "components/FixedTable";
import IconButton from "components/IconButton";
import SmallAlert from "components/SmallAlert";
import Stack from "components/Stack";
import Text from "components/Text";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import { saveTableData } from "entities/Field/components/FixedTableField/utils";

interface IFixedTableFieldEditButton {
  field: {
    display_name: string;
    header_name: Array<{ headerName: string; field: string }>;
  };
  initialValue: Array<{ [key: string]: string }>;
  onSave: ({ newValue }: { newValue: any }) => Promise<any> | void;
  onUpdate: ({ message }: { message?: string }) => void;
  lastSavedAt: string | null;
  visible: boolean;
  buttonDataTestid?: string;
}

const FixedTableFieldEditButton: React.FC<IFixedTableFieldEditButton> = ({
  field,
  initialValue,
  onSave,
  onUpdate,
  buttonDataTestid,
  lastSavedAt,
  visible,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useCaching(initialValue);
  const [saveTimer, setSaveTimer] = useState<NodeJS.Timeout | null>(null);

  const { show: showConfirmationDialog } = useConfirmationDialog();
  const handleEditButtonClick = useCallback(() => setIsOpen(true), []);

  const handleSave = useCallback(() => {
    const saveResult = onSave({ newValue: value });

    if (typeof saveResult?.then === "function") {
      saveResult.then(() => {
        setIsOpen(false);
        onUpdate({ message: `${field.display_name} successfully updated!` });

        if (!_.isNull(saveTimer)) {
          clearTimeout(saveTimer);
        }
      });
    } else {
      setIsOpen(false);
      onUpdate({ message: `${field.display_name} successfully updated!` });

      if (!_.isNull(saveTimer)) {
        clearTimeout(saveTimer);
      }
    }
  }, [setIsOpen, onSave, value, onUpdate, field.display_name, saveTimer]);

  const handleCancel = useCallback(() => {
    showConfirmationDialog({
      confirmButtonColor: "primary",
      onConfirm: () => {
        if (!_.isNull(saveTimer)) {
          clearTimeout(saveTimer);
        }

        setValue(initialValue);
        setIsOpen(false);
      },
      confirmButtonText: "Confirm",
      message:
        "Are you sure you want to exit? Your changes might not be saved.",
    });
  }, [setIsOpen, setValue, initialValue, saveTimer, showConfirmationDialog]);

  const handleChange = useCallback(
    (newValue) => {
      setValue(newValue);

      // Save the data every 3 seconds of inactivity.
      const newTimer = setTimeout(() => {
        saveTableData({ newValue, onSave, onUpdate });
      }, 3000);

      setSaveTimer((prevTimer) => {
        if (!_.isNull(prevTimer)) {
          clearTimeout(prevTimer);
        }

        return newTimer;
      });
    },
    [onSave, setValue, onUpdate]
  );

  return (
    <>
      {visible && (
        <IconButton
          size="small"
          onClick={handleEditButtonClick}
          dataTestid={buttonDataTestid}
        >
          <EditIcon />
        </IconButton>
      )}
      {isOpen && (
        <Dialog open={isOpen} maxWidth="lg" fullWidth onClose={handleCancel}>
          <Box
            sx={{
              padding: theme.spacing(1),
            }}
            data-testid="table-field-edit-dialog"
          >
            <DialogTitle data-testid="table-field-dialog-title">
              {field.display_name}
            </DialogTitle>
            <Stack spacing={1} sx={{ paddingX: theme.spacing(3) }}>
              <FixedTable
                rows={value}
                headers={(field.header_name || []).map(
                  ({ headerName, field }) => ({
                    label: headerName,
                    key: field,
                  })
                )}
                onUpdated={handleChange}
                editable
                showDeleteCell
              />
            </Stack>
            <DialogActions
              sx={{
                padding: 2,
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <SmallAlert title="Don’t forget to save your changes after editing!" />
                {!_.isNil(lastSavedAt) && (
                  <Text
                    variant="text3"
                    component="span"
                    sx={{ color: colors.gray80 }}
                    data-testid="last-saved-timestamp"
                  >
                    Last saved at{" "}
                    {format(parseISO(lastSavedAt), FORMATS.FRONTEND.DATETIME)}
                  </Text>
                )}
              </Stack>

              <Stack direction="row" spacing={1}>
                <Button
                  variant="text"
                  onClick={handleCancel}
                  data-testid="cancel-button"
                >
                  Cancel
                </Button>
                <Button onClick={handleSave} data-testid="submit-button">
                  Save
                </Button>
              </Stack>
            </DialogActions>
          </Box>
        </Dialog>
      )}
    </>
  );
};

export default FixedTableFieldEditButton;
